import React, { useState, useEffect, useMemo } from 'react'
import { Link, useSearchParams, useNavigate } from 'react-router-dom'
import { useAuth0 } from '@auth0/auth0-react'
import { toast } from 'react-toastify'
import * as Sentry from '@sentry/react'

const { logger } = Sentry;
import { 
  getUserData, 
  saveJob,
  getJobs,
  getJob,
  updateJobState,
  getJobActivities,
  deleteJob,
  getProfile,
  getResumeGenerationStatus,
  AuthenticationRequiredError,
  InsufficientCreditsError
} from '../../services/api'
import ContributionHeatmap from './ContributionHeatmap'
import JobPostingCard from './JobPostingCard'
import ImportJobModal from './ImportJobModal'
import FindJobsModal from './FindJobsModal'
import ActivityStream from './ActivityStream'
import AccountDeletionWarning from '../common/AccountDeletionWarning'
import PurchaseCreditsModal from '../common/PurchaseCreditsModal'
import WelcomeScreen from './WelcomeScreen'
import { trackJobImport, trackJobStateChange, trackJobDeletion, trackLogin } from '../../utils/analytics'
import { getStoredReferralCode, clearReferralCode } from '../../utils/referral'
import { updateUser } from '../../services/api'
import './Dashboard.scss'

/**
 * Normalize activity data from backend to frontend format
 * Maps 'type' (e.g., 'job_imported') to 'state' (e.g., 'imported')
 * Maps 'title' to 'jobTitle'
 */
const normalizeActivities = (activities: any[]): any[] => {
  if (!Array.isArray(activities)) {
    return []
  }
  return activities.map(activity => {
    // Convert Unix timestamp (seconds) to ISO string if needed
    let timestamp = activity.timestamp
    if (activity.createdAt && typeof activity.createdAt === 'number') {
      // Unix timestamp in seconds, convert to milliseconds for Date
      timestamp = new Date(activity.createdAt * 1000).toISOString()
    } else if (!timestamp && activity.createdAt) {
      timestamp = activity.createdAt
    }
    
    return {
      ...activity,
      // Map 'type' (e.g., 'job_imported') to 'state' (e.g., 'imported')
      state: activity.type ? activity.type.replace(/^job_/, '') : activity.state,
      // Map 'title' to 'jobTitle' for consistency
      jobTitle: activity.title || activity.jobTitle,
      // Ensure timestamp is set
      timestamp: timestamp || activity.timestamp || new Date().toISOString()
    }
  })
}

function Dashboard() {
  const { user, isLoading, getAccessTokenSilently, loginWithRedirect } = useAuth0()
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  
  // Test mode: provide mock user if in test mode
  const isTestMode = typeof window !== 'undefined' && 
    (localStorage.getItem('__TEST_MODE__') === 'true' || 
     window.location.search.includes('testMode=true'));
  const testUser = isTestMode ? {
    email: 'test@example.com',
    name: 'Test User',
    picture: 'https://via.placeholder.com/150',
    sub: 'auth0|test123',
  } : null;
  const effectiveUser = user || testUser;
  const effectiveIsLoading = isTestMode ? false : isLoading;
  
  const [userData, setUserData] = useState<any>(null)
  const [loadingUserData, setLoadingUserData] = useState(true)
  const [profileData, setProfileData] = useState<any>(null)
  const [loadingProfile, setLoadingProfile] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [jobPostings, setJobPostings] = useState<any[]>([])
  const [loadingJobPostings, setLoadingJobPostings] = useState(false)
  const [activities, setActivities] = useState<any[]>([])
  const [loadingActivities, setLoadingActivities] = useState(false)
  const [resumeStatuses, setResumeStatuses] = useState<Record<string, any>>({})
  const [showImportModal, setShowImportModal] = useState(false)
  const [showFindJobsModal, setShowFindJobsModal] = useState(false)
  const [isImporting, setIsImporting] = useState(false)
  const [showPurchaseModal, setShowPurchaseModal] = useState(false)
  // Persist FindJobsModal state across Dashboard re-renders
  const [findJobsModalJobs, setFindJobsModalJobs] = useState<any[]>([])
  const [findJobsModalKeywords, setFindJobsModalKeywords] = useState<string>('')
  const [importedJobUrls, setImportedJobUrls] = useState<Set<string>>(new Set())
  
  // Sort and filter state
  const [sortBy, setSortBy] = useState<'imported' | 'updated' | 'state'>('updated')
  const [filterByState, setFilterByState] = useState<string>('')
  const [filterByCompany, setFilterByCompany] = useState<string>('')

  // Handle payment success/cancel redirects from Stripe
  useEffect(() => {
    const success = searchParams.get('success')
    const canceled = searchParams.get('canceled')
    const sessionId = searchParams.get('session_id')

    // Use a ref to prevent duplicate toasts (React strict mode can cause double renders)
    if (success === 'true' && sessionId) {
      // Check if we've already shown this toast (prevent duplicates)
      const toastKey = `payment-success-${sessionId}`
      if (!sessionStorage.getItem(toastKey)) {
        sessionStorage.setItem(toastKey, 'true')
        toast.success('Payment successful! Your credits have been added to your account.')
        
        // Clean up after 5 seconds
        setTimeout(() => {
          sessionStorage.removeItem(toastKey)
        }, 5000)
      }
      
      // Clean up URL by removing query parameters
      navigate('/dashboard', { replace: true })
      
      // Refresh user data to show updated credits
      if (effectiveUser && !effectiveIsLoading) {
        const tokenFn = isTestMode 
          ? async () => 'mock-test-token'
          : getAccessTokenSilently;
        getUserData(tokenFn).then(data => {
          setUserData(data)
        }).catch(err => {
          logger.error('Error refreshing user data after payment', {
            error: err instanceof Error ? err.message : String(err),
          });
        })
      }
    } else if (canceled === 'true') {
      toast.info('Payment was canceled.')
      // Clean up URL
      navigate('/dashboard', { replace: true })
    }
  }, [searchParams, navigate, effectiveUser, effectiveIsLoading, getAccessTokenSilently, isTestMode])

  // Extract userId early for use in all data fetching hooks
  const userId = effectiveUser?.sub

  // Fetch user data from backend when user is authenticated - use userId as stable dependency
  const userDataFetchedRef = React.useRef<string | undefined>(undefined)
  const userDataFetchingRef = React.useRef(false) // Prevent concurrent fetches
  useEffect(() => {
    const fetchUserData = async () => {
      // Skip if we already fetched for this user or are currently fetching
      if (userDataFetchedRef.current === userId || userDataFetchingRef.current) {
        return
      }
      
      if (isTestMode) {
        // In test mode, load immediately
        const tokenFn = async () => 'mock-test-token';
        try {
          setLoadingUserData(true);
          const data = await getUserData(tokenFn);
          setUserData(data);
        } catch (err) {
          logger.error('Error loading user data in test mode', {
            error: err instanceof Error ? err.message : String(err),
          });
          setError(err instanceof Error ? err.message : 'Failed to load user data');
        } finally {
          setLoadingUserData(false);
        }
      } else if (!effectiveIsLoading && userId) {
        userDataFetchingRef.current = true
        userDataFetchedRef.current = userId
        try {
          setLoadingUserData(true)
          setError(null)
          // Backend automatically creates user if they don't exist
          const tokenFn = isTestMode 
            ? async () => 'mock-test-token'
            : getAccessTokenSilently;
          const data = await getUserData(tokenFn)
          
          // Check for referral code and apply it if user is new (just created)
          // Backend checks if user was created within last 60 seconds to determine if they're new
          const referralCode = getStoredReferralCode()
          if (referralCode && !data.referredBy) {
            try {
              // Try to apply the referral code (backend will only accept it for new users)
              const tokenFn = isTestMode 
                ? async () => 'mock-test-token'
                : getAccessTokenSilently;
              await updateUser({ referralCode }, tokenFn)
              // Clear the stored referral code after using it
              clearReferralCode()
              // Reload user data to get updated info
              const updatedData = await getUserData(tokenFn)
              setUserData(updatedData)
            } catch (refErr) {
              logger.error('Error applying referral code', {
                error: refErr instanceof Error ? refErr.message : String(refErr),
              });
              // Continue with original data even if referral fails
              setUserData(data)
            }
          } else {
            setUserData(data)
          }
          
          // Track authentication/login when user data is successfully loaded
          trackLogin('auth0')
        } catch (err) {
          logger.error('Error fetching user data', {
            error: err instanceof Error ? err.message : String(err),
          });
          
          // Check if this is an authentication required error (consent_required, login_required)
          if (err instanceof AuthenticationRequiredError) {
            // Trigger re-login
            loginWithRedirect({
              authorizationParams: {
                prompt: 'login'
              },
              appState: {
                returnTo: window.location.pathname
              }
            })
            return // Don't set error state, we're redirecting
          }
          
          const errorMessage = err instanceof Error ? err.message : 'Failed to load user data'
          setError(errorMessage)
          // Reset ref on error so we can retry
          userDataFetchedRef.current = undefined
        } finally {
          setLoadingUserData(false)
          userDataFetchingRef.current = false
        }
      }
    }

    fetchUserData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, effectiveIsLoading, isTestMode]) // Only depend on userId, not the function

  // Fetch profile data to check for experiences - use userId as stable dependency
  const profileFetchedRef = React.useRef<string | undefined>(undefined)
  const profileFetchingRef = React.useRef(false) // Prevent concurrent fetches
  useEffect(() => {
    const fetchProfileData = async () => {
      // Skip if we already fetched for this user or are currently fetching
      if (profileFetchedRef.current === userId || profileFetchingRef.current) {
        return
      }
      
      if (isTestMode) {
        // In test mode, load immediately
        const tokenFn = async () => 'mock-test-token';
        try {
          setLoadingProfile(true);
          const profile = await getProfile(tokenFn);
          setProfileData(profile);
        } catch (err) {
          logger.error('Error loading profile in test mode', {
            error: err instanceof Error ? err.message : String(err),
          });
          setProfileData(null);
        } finally {
          setLoadingProfile(false);
        }
      } else if (!effectiveIsLoading && userId) {
        profileFetchingRef.current = true
        profileFetchedRef.current = userId
        try {
          setLoadingProfile(true);
          const tokenFn = isTestMode 
            ? async () => 'mock-test-token'
            : getAccessTokenSilently;
          const profile = await getProfile(tokenFn);
          setProfileData(profile);
        } catch (err) {
          logger.error('Error fetching profile data', {
            error: err instanceof Error ? err.message : String(err),
          });
          // Profile might not exist yet, which is fine
          setProfileData(null);
          // Reset ref on error so we can retry
          profileFetchedRef.current = undefined
        } finally {
          setLoadingProfile(false);
          profileFetchingRef.current = false
        }
      }
    }

    fetchProfileData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, effectiveIsLoading, isTestMode]) // Only depend on userId, not the function
  
  // Fetch job postings - use userId as stable dependency
  const jobsFetchedRef = React.useRef<string | undefined>(undefined)
  const jobsFetchingRef = React.useRef(false) // Prevent concurrent fetches
  useEffect(() => {
    const fetchJobPostings = async () => {
      // Skip if we already fetched for this user or are currently fetching
      if (jobsFetchedRef.current === userId || jobsFetchingRef.current) {
        return
      }
      
      if (isTestMode) {
        // In test mode, load immediately
        const tokenFn = async () => 'mock-test-token';
        try {
          setLoadingJobPostings(true);
          const jobs = await getJobs(tokenFn);
          setJobPostings(jobs);
          jobsFetchedRef.current = userId
        } catch (err) {
          logger.error('Error loading jobs in test mode', {
            error: err instanceof Error ? err.message : String(err),
          });
        } finally {
          setLoadingJobPostings(false);
        }
      } else if (!effectiveIsLoading && userId) {
        jobsFetchingRef.current = true
        jobsFetchedRef.current = userId
        try {
          setLoadingJobPostings(true)
          const data = await getJobs(getAccessTokenSilently)
          setJobPostings(Array.isArray(data) ? data : [])
        } catch (err) {
          // Reset ref on error so we can retry
          jobsFetchedRef.current = undefined
          if (err instanceof AuthenticationRequiredError) {
            // Will be handled by the user data fetch error handler
            return
          }
          logger.error('Error fetching job postings', {
            error: err instanceof Error ? err.message : String(err),
          });
          setJobPostings([])
        } finally {
          setLoadingJobPostings(false)
          jobsFetchingRef.current = false
        }
      }
    }

    fetchJobPostings()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, effectiveIsLoading, isTestMode]) // Only depend on userId, not the function

  // Fetch resume generation status once (shared across all job cards)
  // Poll for updates when there are active resume generations
  // Only fetch when user is available and not loading, using user ID as stable dependency
  const resumeStatusFetchedRef = React.useRef<string | undefined>(undefined)
  const resumeStatusFetchingRef = React.useRef(false) // Prevent concurrent fetches
  const resumeStatusPollIntervalRef = React.useRef<ReturnType<typeof setInterval> | null>(null)
  const resumeStatusesRef = React.useRef<Record<string, any>>({}) // Keep ref of current statuses for polling check
  
  useEffect(() => {
    const fetchResumeStatus = async () => {
      // Skip if we're currently fetching
      if (resumeStatusFetchingRef.current) {
        return
      }
      
      if (isTestMode) {
        // In test mode, skip
        return
      } else if (!effectiveIsLoading && userId) {
        resumeStatusFetchingRef.current = true
        resumeStatusFetchedRef.current = userId
        try {
          const response = await getResumeGenerationStatus(getAccessTokenSilently)
          // Handle both old format (single status) and new format (statuses map)
          let newStatuses: Record<string, any> = {}
          if ('statuses' in response) {
            // New format: map of jobId -> status
            newStatuses = response.statuses || {}
          } else {
            // Old format: single status (backward compatibility)
            // Convert to map format
            const status = response as any
            if (status.jobId) {
              newStatuses = { [status.jobId]: status }
            }
          }
          setResumeStatuses(newStatuses)
          resumeStatusesRef.current = newStatuses // Update ref
        } catch (err) {
          // Silently fail - resume might not be generated yet
          logger.debug('Resume status not available', {
            error: err instanceof Error ? err.message : String(err),
          });
          setResumeStatuses({})
          resumeStatusesRef.current = {} // Update ref
          // Reset ref on error so we can retry
          resumeStatusFetchedRef.current = undefined
        } finally {
          resumeStatusFetchingRef.current = false
        }
      }
    }

    // Fetch immediately
    fetchResumeStatus()
    
    // Set up polling for active resume generations (every 10 seconds)
    const pollInterval = 10000 // 10 seconds
    resumeStatusPollIntervalRef.current = setInterval(() => {
      // Only poll if there are active resume generations (processing status)
      // Use ref to get current value without causing re-renders
      const hasActiveGenerations = Object.values(resumeStatusesRef.current).some(
        (status: any) => status?.status === 'processing'
      )
      if (hasActiveGenerations && !resumeStatusFetchingRef.current) {
        fetchResumeStatus()
      }
    }, pollInterval)
    
    // Cleanup polling on unmount or when dependencies change
    return () => {
      if (resumeStatusPollIntervalRef.current) {
        clearInterval(resumeStatusPollIntervalRef.current)
        resumeStatusPollIntervalRef.current = null
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, effectiveIsLoading, isTestMode]) // Only depend on userId, not the function

  // Fetch activity stream - use userId as stable dependency
  const activitiesFetchedRef = React.useRef<string | undefined>(undefined)
  const activitiesFetchingRef = React.useRef(false) // Prevent concurrent fetches
  useEffect(() => {
    const fetchActivities = async () => {
      // Skip if we already fetched for this user or are currently fetching
      if (activitiesFetchedRef.current === userId || activitiesFetchingRef.current) {
        return
      }
      
      if (isTestMode) {
        // In test mode, load immediately
        const tokenFn = async () => 'mock-test-token';
        try {
          setLoadingActivities(true);
          const data = await getJobActivities(tokenFn);
          const normalized = normalizeActivities(data);
          const sorted = normalized.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
          setActivities(sorted);
        } catch (err) {
          logger.error('Error loading activities in test mode', {
            error: err instanceof Error ? err.message : String(err),
          });
          setActivities([]);
        } finally {
          setLoadingActivities(false);
        }
      } else if (!effectiveIsLoading && userId) {
        activitiesFetchingRef.current = true
        activitiesFetchedRef.current = userId
        try {
          setLoadingActivities(true)
          const data = await getJobActivities(getAccessTokenSilently)
          // Normalize activities and sort by timestamp descending (most recent first)
          const normalized = normalizeActivities(data)
          const sorted = normalized.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
          setActivities(sorted)
        } catch (err) {
          // Reset ref on error so we can retry
          activitiesFetchedRef.current = undefined
          if (err instanceof AuthenticationRequiredError) {
            // Will be handled by the user data fetch error handler
            return
          }
          logger.error('Error fetching activities', {
            error: err instanceof Error ? err.message : String(err),
          });
          setActivities([])
        } finally {
          setLoadingActivities(false)
          activitiesFetchingRef.current = false
        }
      }
    }

    fetchActivities()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, effectiveIsLoading, isTestMode]) // Only depend on userId, not the function

  // Poll jobs in "imported" state until they're analyzed
  useEffect(() => {
    if (effectiveIsLoading || !effectiveUser) return

    const importedJobs = jobPostings.filter(job => job.state === 'imported')
    if (importedJobs.length === 0) return

    const pollInterval = 10000 // Poll every 10 seconds
    const maxPollAttempts = 60 // Stop after 10 minutes (60 * 10s)
    const pollAttempts = new Map<string, number>()
    const jitterMax = 2000 // Maximum jitter of 2 seconds

    const pollJob = async (jobId: string) => {
      try {
        const job = await getJob(jobId, getAccessTokenSilently)
        if (job && job.state !== 'imported') {
          // Job is no longer in imported state, update it in the list immediately
          // This ensures the progress tracker updates right away
          setJobPostings(prevJobs => {
            const updatedJobs = prevJobs.map(j => 
              j.id === jobId ? { ...j, ...job } : j
            )
            // If job not found in list, add it (shouldn't happen, but just in case)
            const jobExists = updatedJobs.some(j => j.id === jobId)
            if (!jobExists) {
              updatedJobs.push(job)
            }
            return updatedJobs
          })
          pollAttempts.delete(jobId)
          
          // Also refresh activities to show the state change
          try {
            const activityData = await getJobActivities(getAccessTokenSilently)
            const normalized = normalizeActivities(activityData)
            const sorted = normalized.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
            setActivities(sorted)
          } catch (err) {
            logger.error('Error refreshing activities after state change', {
              error: err instanceof Error ? err.message : String(err),
            });
          }
        } else if (job && job.state === 'imported') {
          // Still importing, check if we've exceeded max attempts
          const attempts = (pollAttempts.get(jobId) || 0) + 1
          pollAttempts.set(jobId, attempts)
          if (attempts >= maxPollAttempts) {
            logger.warn('Stopped polling job after max attempts', {
              jobId,
              maxPollAttempts,
            });
            pollAttempts.delete(jobId)
          }
        }
      } catch (error) {
        logger.error('Error polling job', {
          error: error instanceof Error ? error.message : String(error),
          jobId,
        });
        // Remove from polling if there's an error
        pollAttempts.delete(jobId)
      }
    }

    const pollAllImportedJobs = () => {
      importedJobs.forEach((job) => {
        if (!pollAttempts.has(job.id) || pollAttempts.get(job.id)! < maxPollAttempts) {
          // Add jitter: each job gets a random delay between 0 and jitterMax milliseconds
          // This spreads out requests to prevent thundering herd
          const jitter = Math.random() * jitterMax
          setTimeout(() => {
            pollJob(job.id)
          }, jitter)
        }
      })
    }

    // Poll immediately
    pollAllImportedJobs()

    // Set up interval polling
    const intervalId = setInterval(pollAllImportedJobs, pollInterval)

    return () => {
      clearInterval(intervalId)
      pollAttempts.clear()
    }
  }, [jobPostings, user, isLoading, getAccessTokenSilently])

  // Refresh data after state changes
  const refreshData = async () => {
    try {
      const [postings, activityData] = await Promise.all([
        getJobs(getAccessTokenSilently),
        getJobActivities(getAccessTokenSilently)
      ])
      setJobPostings(Array.isArray(postings) ? postings : [])
      // Normalize activities and sort by timestamp descending
      const normalized = normalizeActivities(activityData)
      const sorted = normalized.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
      setActivities(sorted)
    } catch (err) {
      logger.error('Error refreshing data', {
        error: err instanceof Error ? err.message : String(err),
      });
    }
  }

  const handleImportJob = async (jobData: any) => {
    setIsImporting(true)
    try {
      // Save job posting to /users/me/jobs endpoint in DynamoDB
      await saveJob(jobData, getAccessTokenSilently)
      // Track job import event
      trackJobImport(jobData.title, jobData.company)
      
      // Refresh user data to show updated credits, job list, and activity stream
      // Note: We don't close the FindJobsModal here - let user close it manually
      setLoadingUserData(true)
      setLoadingJobPostings(true)
      setLoadingActivities(true)
      try {
        const [userDataResponse, jobsData, activitiesData] = await Promise.all([
          getUserData(getAccessTokenSilently),
          getJobs(getAccessTokenSilently),
          getJobActivities(getAccessTokenSilently)
        ])
        setUserData(userDataResponse)
        setJobPostings(Array.isArray(jobsData) ? jobsData : [])
        // Normalize activities and sort by timestamp descending
        const normalized = normalizeActivities(activitiesData)
        const sorted = normalized.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
        setActivities(sorted)
      } catch (err) {
        logger.error('Error refreshing data', {
          error: err instanceof Error ? err.message : String(err),
        });
        // Still show success even if refresh fails
      } finally {
        setLoadingUserData(false)
        setLoadingJobPostings(false)
        setLoadingActivities(false)
      }
    } catch (err) {
      // Don't log expected client errors (validation errors, insufficient credits, etc.)
      const errorMessage = err instanceof Error ? err.message : String(err);
      const isClientError = 
        err instanceof InsufficientCreditsError ||
        errorMessage.includes('400') ||
        errorMessage.includes('Bad Request') ||
        errorMessage.includes('validation') ||
        errorMessage.includes('required');
      
      if (!isClientError) {
        // Only log unexpected errors
        logger.error('Error importing job', {
          error: errorMessage,
        });
      }
      
      // Handle insufficient credits error
      if (err instanceof InsufficientCreditsError) {
        toast.error(err.message || 'You have no credits remaining. Please purchase more credits to import job postings.')
        setShowPurchaseModal(true)
        throw err
      }
      throw err
    } finally {
      setIsImporting(false)
    }
  }

  const handleStateChange = async (jobId: string, newState: string) => {
    try {
      // Find the job to get its current state and details
      const job = jobPostings.find(j => j.id === jobId)
      const oldState = job?.state || 'unknown'
      
      await updateJobState(jobId, newState, getAccessTokenSilently)
      
      // Track state change event
      trackJobStateChange(oldState, newState, job?.title)
      
      await refreshData()
    } catch (err) {
      logger.error('Error updating state', {
        error: err instanceof Error ? err.message : String(err),
      });
      throw err
    }
  }

  const handleDeleteJob = async (jobId: string) => {
    try {
      // Find the job to get its details before deleting
      const job = jobPostings.find(j => j.id === jobId)
      
      await deleteJob(jobId, getAccessTokenSilently)
      await refreshData()
      
      // Track job deletion event
      trackJobDeletion(job?.title, job?.company)
    } catch (err) {
      logger.error('Error deleting job posting', {
        error: err instanceof Error ? err.message : String(err),
      });
      throw err
    }
  }

  // Get unique companies for filter dropdown
  const uniqueCompanies = useMemo(() => {
    const companies = new Set<string>()
    jobPostings.forEach(job => {
      if (job.company) {
        companies.add(job.company)
      }
    })
    return Array.from(companies).sort()
  }, [jobPostings])

  // Get unique states for filter dropdown
  const uniqueStates = useMemo(() => {
    const states = new Set<string>()
    jobPostings.forEach(job => {
      if (job.state) {
        states.add(job.state)
      }
    })
    return Array.from(states).sort()
  }, [jobPostings])

  // Filter and sort jobs
  const filteredAndSortedJobs = useMemo(() => {
    let filtered = [...jobPostings]

    // Apply state filter
    if (filterByState) {
      filtered = filtered.filter(job => job.state === filterByState)
    }

    // Apply company filter
    if (filterByCompany) {
      filtered = filtered.filter(job => job.company === filterByCompany)
    }

    // Apply sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'imported':
          const aCreated = new Date(a.createdAt || 0).getTime()
          const bCreated = new Date(b.createdAt || 0).getTime()
          return bCreated - aCreated // Newest first
        case 'updated':
          const aUpdated = new Date(a.updatedAt || a.createdAt || 0).getTime()
          const bUpdated = new Date(b.updatedAt || b.createdAt || 0).getTime()
          return bUpdated - aUpdated // Newest first
        case 'state':
          const stateOrder: Record<string, number> = {
            'imported': 1,
            'analyzing': 2,
            'analyzed': 3,
            'tailor_made': 4,
            'resume_created': 4, // Legacy state, map to tailor_made
            'applied': 5,
            'offer_received': 6,
            'rejected': 6,
            'outcome': 6,
            'no_offer': 6, // Legacy state
            'offer_rejected': 6, // Legacy state
            'offer_accepted': 6 // Legacy state
          }
          const aStateOrder = stateOrder[a.state] || 999
          const bStateOrder = stateOrder[b.state] || 999
          if (aStateOrder !== bStateOrder) {
            return aStateOrder - bStateOrder
          }
          // If same state, sort by updated date
          const aUpdatedState = new Date(a.updatedAt || a.createdAt || 0).getTime()
          const bUpdatedState = new Date(b.updatedAt || b.createdAt || 0).getTime()
          return bUpdatedState - aUpdatedState
        default:
          // Default to sorting by updated time (descending)
          const aUpdatedDefault = new Date(a.updatedAt || a.createdAt || 0).getTime()
          const bUpdatedDefault = new Date(b.updatedAt || b.createdAt || 0).getTime()
          return bUpdatedDefault - aUpdatedDefault
      }
    })

    return filtered
  }, [jobPostings, sortBy, filterByState, filterByCompany])

  // Check if user has at least one experience
  const hasExperiences = profileData && 
    Array.isArray(profileData.experiences) && 
    profileData.experiences.length > 0

  // In test mode, don't show loading spinner
  if (!isTestMode && (effectiveIsLoading || loadingUserData || loadingProfile)) {
    return (
      <div className="container py-5">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    )
  }

  // Show welcome screen if user doesn't have any experiences
  if (!loadingProfile && !hasExperiences) {
    return <WelcomeScreen />
  }

  return (
    <div className="dashboard-page">
      <div className="dashboard-wrapper">
            <div className="dashboard-container">
              <div className="dashboard-header">
                <div className="header-content">
                  <h1 className="dashboard-title">
                    Welcome to JobsReady<span className="ai-gradient">AI</span>
                  </h1>
                  <p className="dashboard-subtitle">
                    {user?.name || user?.email}
                  </p>
                </div>
              </div>

              <AccountDeletionWarning user={userData} />

              {/* Heatmap - Full Width */}
              <div className="dashboard-section">
                <div className="dashboard-card heatmap-card">
                  <ContributionHeatmap activities={activities} />
                </div>
              </div>

              {/* Job Postings and Activity Stream - Two Column Layout */}
              <div className="dashboard-grid">
                {/* Left Column - Job Postings */}
                <div className="dashboard-main">
                  <div className="dashboard-card">
                    <div className="card-header">
                      <div className="card-header-content">
                        <h3 className="card-title">Job Postings</h3>
                        {jobPostings.length > 0 && (
                          <span className="card-badge">{jobPostings.length}</span>
                        )}
                      </div>
                      {userData && (userData.credits || 0) <= 0 ? (
                        <div className="card-header-actions">
                          <button 
                            className="btn-modern btn-primary"
                            onClick={() => setShowPurchaseModal(true)}
                            disabled={!userData}
                          >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect>
                              <line x1="1" y1="10" x2="23" y2="10"></line>
                            </svg>
                            Purchase Credits
                          </button>
                          <small className="credits-warning">No credits remaining</small>
                        </div>
                      ) : (
                        <div className="d-flex gap-2 flex-wrap">
                          <button 
                            className="btn-modern btn-primary"
                            onClick={() => setShowImportModal(true)}
                            disabled={!userData || (userData.credits || 0) <= 0}
                          >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <line x1="12" y1="5" x2="12" y2="19"></line>
                              <line x1="5" y1="12" x2="19" y2="12"></line>
                            </svg>
                            Import Job Posting
                          </button>
                          <button 
                            className="btn-modern btn-outline-primary"
                            onClick={() => setShowFindJobsModal(true)}
                            disabled={!userData || (userData.credits || 0) <= 0}
                          >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <circle cx="11" cy="11" r="8"></circle>
                              <path d="m21 21-4.35-4.35"></path>
                            </svg>
                            Find Jobs for Me
                          </button>
                        </div>
                      )}
                    </div>

                    {loadingJobPostings ? (
                      <div className="loading-state">
                        <div className="spinner"></div>
                        <p>Loading job postings...</p>
                      </div>
                    ) : jobPostings.length === 0 ? (
                      <div className="empty-state">
                        <div className="empty-icon">
                          <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                            <polyline points="14 2 14 8 20 8"></polyline>
                            <line x1="16" y1="13" x2="8" y2="13"></line>
                            <line x1="16" y1="17" x2="8" y2="17"></line>
                            <polyline points="10 9 9 9 8 9"></polyline>
                          </svg>
                        </div>
                        <h4 className="empty-title">No job postings yet</h4>
                        <p className="empty-description">
                          {userData && (userData.credits || 0) <= 0 
                            ? 'You have no credits remaining. Purchase credits to import job postings!'
                            : 'Import your first job posting to get started!'
                          }
                        </p>
                        {userData && (userData.credits || 0) <= 0 ? (
                          <button 
                            className="btn-modern btn-primary"
                            onClick={() => setShowPurchaseModal(true)}
                          >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect>
                              <line x1="1" y1="10" x2="23" y2="10"></line>
                            </svg>
                            Purchase Credits
                          </button>
                        ) : (
                          <div className="d-flex gap-2 flex-wrap justify-content-center">
                            <button 
                              className="btn-modern btn-primary"
                              onClick={() => setShowImportModal(true)}
                              disabled={!userData || (userData.credits || 0) <= 0}
                            >
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <line x1="12" y1="5" x2="12" y2="19"></line>
                                <line x1="5" y1="12" x2="19" y2="12"></line>
                              </svg>
                              Import Your First Job Posting
                            </button>
                            <button 
                              className="btn-modern btn-outline-primary"
                              onClick={() => setShowFindJobsModal(true)}
                              disabled={!userData || (userData.credits || 0) <= 0}
                              style={{ width: 'auto', flexShrink: 0, flexGrow: 0 }}
                            >
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <circle cx="11" cy="11" r="8"></circle>
                                <path d="m21 21-4.35-4.35"></path>
                              </svg>
                              Find Jobs for Me
                            </button>
                          </div>
                        )}
                      </div>
                    ) : (
                      <>
                        {/* Sort and Filter Controls */}
                        <div className="job-filters">
                          <div className="filters-grid">
                            {/* Sort Dropdown */}
                            <div className="filter-group">
                              <label className="filter-label">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <polyline points="6 9 12 15 18 9"></polyline>
                                </svg>
                                Sort by
                              </label>
                              <select
                                className="filter-select"
                                value={sortBy}
                                onChange={(e) => setSortBy(e.target.value as 'imported' | 'updated' | 'state')}
                              >
                                <option value="updated">Last Updated</option>
                                <option value="imported">Imported Date</option>
                                <option value="state">Status</option>
                              </select>
                            </div>

                            {/* Status Filter */}
                            <div className="filter-group">
                              <label className="filter-label">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <circle cx="12" cy="12" r="10"></circle>
                                  <polyline points="12 6 12 12 16 14"></polyline>
                                </svg>
                                Status
                              </label>
                              <div className="filter-input-wrapper">
                                <select
                                  className="filter-select"
                                  value={filterByState}
                                  onChange={(e) => setFilterByState(e.target.value)}
                                >
                                  <option value="">All Statuses</option>
                                  {uniqueStates.map(state => (
                                    <option key={state} value={state}>
                                      {state.charAt(0).toUpperCase() + state.slice(1).replace(/_/g, ' ')}
                                    </option>
                                  ))}
                                </select>
                                {filterByState && (
                                  <button
                                    className="filter-clear-btn"
                                    type="button"
                                    onClick={() => setFilterByState('')}
                                    title="Clear status filter"
                                    aria-label="Clear status filter"
                                  >
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                      <line x1="18" y1="6" x2="6" y2="18"></line>
                                      <line x1="6" y1="6" x2="18" y2="18"></line>
                                    </svg>
                                  </button>
                                )}
                              </div>
                            </div>

                            {/* Company Filter */}
                            <div className="filter-group">
                              <label className="filter-label">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                                  <circle cx="12" cy="10" r="3"></circle>
                                </svg>
                                Company
                              </label>
                              <div className="filter-input-wrapper">
                                <select
                                  className="filter-select"
                                  value={filterByCompany}
                                  onChange={(e) => setFilterByCompany(e.target.value)}
                                >
                                  <option value="">All Companies</option>
                                  {uniqueCompanies.map(company => (
                                    <option key={company} value={company}>
                                      {company}
                                    </option>
                                  ))}
                                </select>
                                {filterByCompany && (
                                  <button
                                    className="filter-clear-btn"
                                    type="button"
                                    onClick={() => setFilterByCompany('')}
                                    title="Clear company filter"
                                    aria-label="Clear company filter"
                                  >
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                      <line x1="18" y1="6" x2="6" y2="18"></line>
                                      <line x1="6" y1="6" x2="18" y2="18"></line>
                                    </svg>
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Results count */}
                          {(filterByState || filterByCompany) && (
                            <div className="filter-results">
                              <span className="results-text">
                                Showing {filteredAndSortedJobs.length} of {jobPostings.length} job{jobPostings.length !== 1 ? 's' : ''}
                              </span>
                            </div>
                          )}
                        </div>

                        {/* Job List */}
                        {filteredAndSortedJobs.length === 0 ? (
                          <div className="empty-state">
                            <div className="empty-icon">
                              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                <circle cx="11" cy="11" r="8"></circle>
                                <path d="m21 21-4.35-4.35"></path>
                              </svg>
                            </div>
                            <h4 className="empty-title">No jobs found</h4>
                            <p className="empty-description">
                              No jobs match your search criteria. Try adjusting your filters.
                            </p>
                            <button 
                              className="btn-modern btn-secondary"
                              onClick={() => {
                                setFilterByState('')
                                setFilterByCompany('')
                              }}
                            >
                              Clear All Filters
                            </button>
                          </div>
                        ) : (
                          <div className="job-postings-list">
                            {filteredAndSortedJobs.map((job) => (
                              <JobPostingCard
                                key={job.id}
                                jobPosting={job}
                                onStateChange={handleStateChange}
                                onDelete={handleDeleteJob}
                                getAccessTokenSilently={getAccessTokenSilently}
                                userData={userData}
                                resumeStatus={resumeStatuses[job.id] || null}
                              />
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>

                {/* Right Column - Account Balance and Activity Stream */}
                <div className="dashboard-sidebar">
                  <div className="sidebar-content">
                    {/* Account Balance */}
                    <div className="dashboard-card account-card">
                      <div className="card-header">
                        <h3 className="card-title">Account Balance</h3>
                      </div>
                      {error && (
                        <div className="alert-modern alert-warning" role="alert">
                          {error}
                        </div>
                      )}
                      {userData ? (
                        <>
                          <div className="account-balance">
                            <div className="balance-amount">{userData.credits || 0}</div>
                            <p className="balance-label">Available Credits</p>
                          </div>
                          <div className="account-actions">
                            <Link 
                              to="/sharing" 
                              className="referral-link"
                            >
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                                <circle cx="9" cy="7" r="4"></circle>
                                <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                                <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                              </svg>
                              Earn more by inviting friends
                            </Link>
                            <button 
                              className="btn-modern btn-primary btn-full"
                              onClick={() => setShowPurchaseModal(true)}
                            >
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect>
                                <line x1="1" y1="10" x2="23" y2="10"></line>
                              </svg>
                              Purchase Credits
                            </button>
                          </div>
                        </>
                      ) : (
                        <div className="loading-state">
                          <div className="spinner"></div>
                          <p>Loading...</p>
                        </div>
                      )}
                    </div>

                    {/* Activity Stream */}
                    <div className="dashboard-card activity-card" style={{ position: 'sticky', top: '20px' }}>
                      <div className="card-header">
                        <h3 className="card-title">Activity Stream</h3>
                      </div>
                      {loadingActivities ? (
                        <div className="loading-state">
                          <div className="spinner"></div>
                          <p>Loading activities...</p>
                        </div>
                      ) : (
                        <ActivityStream activities={activities} />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
      </div>

      <ImportJobModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        onImport={handleImportJob}
        isImporting={isImporting}
        userCredits={userData?.credits}
      />
      <FindJobsModal
        isOpen={showFindJobsModal}
        onClose={() => {
          setShowFindJobsModal(false)
          // Clear modal state when closing
          setFindJobsModalJobs([])
          setFindJobsModalKeywords('')
          setImportedJobUrls(new Set())
        }}
        onImport={handleImportJob}
        isImporting={isImporting}
        userCredits={userData?.credits}
        jobs={findJobsModalJobs}
        keywords={findJobsModalKeywords}
        onJobsChange={setFindJobsModalJobs}
        onKeywordsChange={setFindJobsModalKeywords}
        importedJobUrls={importedJobUrls}
        onJobImported={(url) => {
          setImportedJobUrls(prev => new Set(prev).add(url))
        }}
      />
      <PurchaseCreditsModal 
        show={showPurchaseModal} 
        onHide={() => setShowPurchaseModal(false)} 
      />
    </div>
  )
}

export default Dashboard

